import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ChallengeStatus } from '@/api/generated';
import { useLanguage } from '@/contexts/language-context';
import {
  loginOtpResendRequestedEvent,
  loginOtpSubmittedEvent,
} from '@/features/login-analytics/telemetry';
import { OtpSubmitMethod } from '@/features/login-analytics/telemetry.enums';
import type { OtpMethod } from '@/features/login-analytics/telemetry.types';
import type { OtpFormData } from '@/form-schemas/otp-schemas';
import { otpFormDefaults, otpSchema } from '@/form-schemas/otp-schemas';
import type { UseMfaOrchestratorReturn } from '@/hooks/use-mfa-orchestrator.types';

interface UseOtpFormProps {
  submitOtp: UseMfaOrchestratorReturn['submitResponse'];
  method: OtpMethod;
  maxRetries?: number;
}

export function useOtpForm({
  submitOtp,
  method,
  maxRetries = 3,
}: UseOtpFormProps) {
  const { t } = useLanguage();
  const [retryCount, setRetryCount] = useState(0);
  const attemptEpochRef = useRef(0);
  const wasPastedRef = useRef(false);

  const form = useForm<OtpFormData>({
    resolver: zodResolver(otpSchema),
    defaultValues: otpFormDefaults,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const { formState, setError, clearErrors } = form;
  const { isSubmitting, errors } = formState;

  const submit = async (data: OtpFormData, submitMethod: OtpSubmitMethod) => {
    const epoch = attemptEpochRef.current;

    wasPastedRef.current = false;

    try {
      clearErrors();

      // Join the code array into a single string
      const otpCode = data.code.join('');

      const response = await submitOtp({
        otp_code: otpCode,
      });

      loginOtpSubmittedEvent({ method, submit_method: submitMethod });

      if (attemptEpochRef.current !== epoch) {
        return;
      }

      const errorStatuses = [
        ChallengeStatus.FAILED,
        ChallengeStatus.REJECTED,
        ChallengeStatus.RETRY_REQUIRED,
      ];

      if (errorStatuses.includes(response as ChallengeStatus)) {
        const next = retryCount + 1;
        setRetryCount(next);
        setError('root', {
          type: 'manual',
          message:
            next >= maxRetries
              ? t('mfa.tooManyAttempts')
              : t('mfa.invalidCode'),
        });
      }
      // If response is FlowStatus.COMPLETED or APPROVED, let it proceed naturally
      // Don't reset form on success - let the page navigate away
    } catch (error) {
      console.error('OTP submission error:', error);

      if (attemptEpochRef.current !== epoch) {
        return;
      }

      setError('root', {
        type: 'manual',
        message: t('mfa.networkError'),
      });
    }
  };

  const onSubmit = (data: OtpFormData) => submit(data, OtpSubmitMethod.BUTTON);

  const resetAttempts = () => {
    attemptEpochRef.current += 1;
    wasPastedRef.current = false;
    setRetryCount(0);
    clearErrors();
    form.reset(otpFormDefaults);
  };

  const handleCodeChange = (newCode: string[]) => {
    clearErrors();
    form.setValue('code', newCode, { shouldValidate: true });
  };

  const handleCodePasted = () => {
    wasPastedRef.current = true;
  };

  const handleComplete = async () => {
    const submitMethod = wasPastedRef.current
      ? OtpSubmitMethod.PASTE
      : OtpSubmitMethod.AUTO_SUBMIT;

    try {
      await form.handleSubmit(data => submit(data, submitMethod))();
    } catch (error) {
      console.error('OTP auto-submit error:', error);
    }
  };

  const handleResendRequested = () => {
    loginOtpResendRequestedEvent({ method });
  };

  const getErrorMessage = () => {
    if (errors.code) {
      return errors.code.message;
    }

    if (errors.root) {
      return errors.root.message;
    }

    return '';
  };

  return {
    form,
    onSubmit,
    isSubmitting,
    errors,
    getErrorMessage,
    hasExceededRetries: retryCount >= maxRetries,
    resetAttempts,
    handleCodeChange,
    handleCodePasted,
    handleComplete,
    handleResendRequested,
  };
}
